/**
 * /portal — casa del miembro logueado.
 *
 * Reemplaza la sala de espera: el miembro entra apenas se registra, sin esperar
 * la aprobación. La aprobación sigue existiendo, pero solo condiciona el link
 * privado de Telegram; nunca bloquea el portal. Lo que falta se muestra adentro.
 *
 * Acá caen también los logueados sin rol admin (`ProtectedAdminRoute` redirige
 * a `USER_PORTAL_PATH`): para ellos el portal es el destino correcto.
 *
 * PWA instalable sin service worker — ver `usePortalPwa`.
 */

import { FullBleedDarkLoading } from "@/components/admin/AdminStates";
import { PortalCard, PortalEyebrow, PortalShell } from "@/components/portal/PortalShell";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { usePortalPwa } from "@/hooks/usePortalPwa";
import { useSEO } from "@/hooks/useSEO";
import {
  getUserDisplayName,
  isProfileComplete,
  useClerkSessionClaims,
  useIsAdmin,
  useTelegramInviteUrl,
} from "@/lib/auth";
import {
  BRAND_PERSON_NAME,
  BRAND_SITE_URL,
  BRAND_SUPPORT_EMAIL,
} from "@/lib/brandConfig";
import { CANONICAL_VIP_PRICE_USD } from "@/lib/canonicalVipMembership";
import { detectCountryCode } from "@/lib/countryDetection";
import { MEMBERSHIP_ROUTE, USER_PORTAL_PATH } from "@/lib/membershipAccessFlow";
import { resolveCheckoutCountryCode } from "@/lib/resolveCheckoutCountryCode";
import { cn } from "@/lib/utils";
import {
  localeForPlansFromMuxLanguage,
  startVipMembershipVideoCheckout,
} from "@/lib/vipMembershipVideoCheckout";
import { RedirectToSignIn, SignOutButton, useAuth, useSession, useUser } from "@clerk/clerk-react";
import {
  ArrowRight,
  Check,
  Copy,
  Crown,
  ExternalLink,
  Loader2,
  LogOut,
  MessageCircle,
  RefreshCw,
  Share,
  ShieldAlert,
  Smartphone,
  Sparkles,
  UserCog,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";

export { USER_PORTAL_PATH } from "@/lib/membershipAccessFlow";

/** Tiers que ya pagaron: no se les ofrece comprar de nuevo. */
const PAID_TIERS = new Set(["vip", "platinum", "devil", "basic", "premium"]);

const SEO = {
  title: `Tu portal — ${BRAND_PERSON_NAME}`,
  description: `Tu membresía con ${BRAND_PERSON_NAME}: acceso, estado y beneficios.`,
  keywords: "",
  ogImage: `${BRAND_SITE_URL}/og.png`,
  canonical: `${BRAND_SITE_URL}${USER_PORTAL_PATH}`,
  robots: "noindex, nofollow" as const,
};

function SectionCard({
  icon: Icon,
  tone = "neutral",
  title,
  children,
}: {
  icon: LucideIcon;
  tone?: "neutral" | "good" | "wait" | "gold";
  title: string;
  children: ReactNode;
}) {
  const toneRing = {
    neutral: "bg-white/[0.06] text-white/70 ring-white/10",
    good: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/25",
    wait: "bg-amber-500/15 text-amber-300 ring-amber-500/25",
    gold: "bg-amber-400/20 text-amber-200 ring-amber-400/30",
  }[tone];
  return (
    <PortalCard highlight={tone === "gold"} className="p-5">
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset",
            toneRing,
          )}
        >
          <Icon className="h-5 w-5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1 space-y-3">
          <h2 className="text-[15px] font-semibold tracking-tight text-white/95">{title}</h2>
          {children}
        </div>
      </div>
    </PortalCard>
  );
}

export default function MemberPortal() {
  useSEO(SEO);
  const location = useLocation();
  const { toast } = useToast();
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const { session } = useSession();
  const isAdmin = useIsAdmin();
  const { claims } = useClerkSessionClaims();
  const telegramUrl = useTelegramInviteUrl();
  const pwa = usePortalPwa();

  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);

  const countryCode = useMemo(
    () => resolveCheckoutCountryCode(detectCountryCode()),
    [],
  );

  /** Relee la membresía del backend y refresca el JWT sin recargar la página. */
  const refreshMembership = useCallback(async () => {
    if (!session || refreshing) return;
    setRefreshing(true);
    try {
      const token = await session.getToken();
      if (token) {
        await fetch("/api/sync-my-membership", {
          headers: { Authorization: `Bearer ${token}` },
        });
        await session.getToken({ skipCache: true });
      }
      toast({ title: "Estado actualizado" });
    } catch {
      toast({
        title: "No se pudo actualizar",
        description: "Revisá la conexión y probá de nuevo.",
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  }, [session, refreshing, toast]);

  const copyAccess = useCallback(async () => {
    if (!telegramUrl) return;
    try {
      await navigator.clipboard.writeText(telegramUrl);
      setCopied(true);
      toast({ title: "Link copiado" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "No se pudo copiar", variant: "destructive" });
    }
  }, [telegramUrl, toast]);

  const startCheckout = useCallback(async () => {
    if (checkoutBusy) return;
    setCheckoutBusy(true);
    try {
      await startVipMembershipVideoCheckout({
        countryCode: countryCode === "UNKNOWN" ? "US" : countryCode,
        pathname: location.pathname,
        languageForPlans: localeForPlansFromMuxLanguage("es"),
        email: user?.primaryEmailAddress?.emailAddress || undefined,
        template: "MemberPortal",
        template_type: "MemberPortal",
        cancelPath: `${USER_PORTAL_PATH}?checkout=cancelado`,
      });
    } catch {
      setCheckoutBusy(false);
      toast({
        title: "No se pudo abrir el checkout",
        description: "Reintentá en unos segundos o escribinos a soporte.",
        variant: "destructive",
      });
    }
  }, [checkoutBusy, countryCode, location.pathname, user, toast]);

  const installApp = useCallback(async () => {
    const outcome = await pwa.install();
    if (outcome === "accepted") toast({ title: "Portal instalado" });
  }, [pwa, toast]);

  if (!isLoaded) return <FullBleedDarkLoading label="Cargando portal…" />;
  if (!isSignedIn) return <RedirectToSignIn />;
  if (isAdmin) return <Navigate to="/admin" replace />;

  const name = getUserDisplayName(user);
  const email = user?.primaryEmailAddress?.emailAddress ?? "";
  const profileComplete = isProfileComplete(user);
  const tier = (claims.membershipLevel || "").toLowerCase();
  const isPaid = PAID_TIERS.has(tier);
  const approved = claims.approved === true || user?.publicMetadata?.approved === true;
  const hasAccessLink = approved && Boolean(telegramUrl);
  const rejected =
    user?.publicMetadata?.rejected === true ||
    claims.membershipStatus === "rejected" ||
    claims.status === "rejected";

  return (
    <PortalShell centered={false}>
      <div className="mx-auto w-full max-w-md space-y-4">
        <header className="space-y-1 px-1">
          <PortalEyebrow>{BRAND_PERSON_NAME || "Portal"}</PortalEyebrow>
          <h1 className="text-[22px] font-semibold tracking-tight text-white/95">
            {name ? `Hola, ${name}` : "Tu portal"}
          </h1>
          <p className="text-[13px] leading-relaxed text-white/50">
            {isPaid
              ? "Sos miembro. Acá tenés tu acceso y tu estado."
              : "Ya estás adentro. Acá activás tu acceso y elegís tu membresía."}
          </p>
        </header>

        {/* Perfil incompleto: lo único que el portal pide antes de seguir. */}
        {!profileComplete ? (
          <SectionCard icon={UserCog} tone="wait" title="Completá tu perfil">
            <p className="text-[13px] leading-relaxed text-white/55">
              Falta tu alias, tu usuario de Telegram y tu fecha de nacimiento. Sin eso no
              podemos prepararte el acceso.
            </p>
            <Button
              asChild
              className="portal-press min-h-11 w-full bg-white font-semibold text-zinc-950 hover:bg-white/90"
            >
              <Link to={MEMBERSHIP_ROUTE.PORTAL_COMPLETAR_REGISTRO} className="gap-2">
                Completar perfil <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </Button>
          </SectionCard>
        ) : null}

        {/* Acceso al canal privado. Cuando todavía no está asignado, se informa
            sin bloquear: el miembro ya está dentro del portal. */}
        {rejected ? (
          <SectionCard icon={ShieldAlert} tone="wait" title="Tu acceso no fue habilitado">
            <p className="text-[13px] leading-relaxed text-white/55">
              Revisamos tu cuenta y por ahora no podemos darte el acceso al canal. Si
              creés que es un error, escribinos y lo miramos de nuevo.
            </p>
            <Button
              asChild
              variant="outline"
              className="portal-press min-h-11 w-full border-white/15 bg-transparent text-white/75 hover:bg-white/5 hover:text-white"
            >
              <a href={`mailto:${BRAND_SUPPORT_EMAIL}`}>Escribir a soporte</a>
            </Button>
          </SectionCard>
        ) : hasAccessLink ? (
          <SectionCard icon={MessageCircle} tone="good" title="Tu acceso está listo">
            <p className="text-[13px] leading-relaxed text-white/55">
              Este es tu link privado. Es personal, no lo compartas.
            </p>
            <div className="space-y-2">
              <Button
                asChild
                className="portal-press min-h-11 w-full bg-emerald-400 font-semibold text-zinc-950 hover:bg-emerald-300"
              >
                <a href={telegramUrl ?? "#"} target="_blank" rel="noopener noreferrer">
                  Abrir el canal <ExternalLink className="ml-2 h-4 w-4" aria-hidden />
                </a>
              </Button>
              <Button
                variant="outline"
                onClick={() => void copyAccess()}
                className="portal-press min-h-11 w-full border-white/15 bg-transparent text-white/75 hover:bg-white/5 hover:text-white"
              >
                {copied ? (
                  <>
                    <Check className="mr-2 h-4 w-4" aria-hidden /> Copiado
                  </>
                ) : (
                  <>
                    <Copy className="mr-2 h-4 w-4" aria-hidden /> Copiar link
                  </>
                )}
              </Button>
            </div>
          </SectionCard>
        ) : (
          <SectionCard icon={MessageCircle} tone="wait" title="Tu acceso al canal">
            <p className="text-[13px] leading-relaxed text-white/55">
              Te estamos preparando el link privado. Lo vas a ver acá mismo y te
              avisamos por mail. No hace falta que esperes en esta pantalla.
            </p>
            <Button
              variant="outline"
              onClick={() => void refreshMembership()}
              disabled={refreshing}
              className="portal-press min-h-11 w-full border-white/15 bg-transparent text-white/75 hover:bg-white/5 hover:text-white"
            >
              {refreshing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> Actualizando…
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" aria-hidden /> Actualizar estado
                </>
              )}
            </Button>
          </SectionCard>
        )}

        {/* Upsell: el motivo de que el portal exista. Se muestra a cualquier
            miembro que todavía no pagó, sin mirar el país: todos cobran por
            Stripe. El país decide la pasarela, nunca si se puede comprar —
            atarlo a otra condición ya dejó a Argentina sin botón una vez. */}
        {!isPaid ? (
          <SectionCard icon={Crown} tone="gold" title="Membresía VIP">
            <p className="text-[13px] leading-relaxed text-white/60">
              Acceso completo, grupo privado y prioridad en todo lo nuevo. Desde{" "}
              <span className="font-semibold text-amber-100">
                US${CANONICAL_VIP_PRICE_USD.toFixed(2)}
              </span>{" "}
              por mes, cancelás cuando quieras.
            </p>
            <Button
              onClick={() => void startCheckout()}
              disabled={checkoutBusy}
              className="portal-press min-h-12 w-full bg-amber-400 text-[15px] font-bold text-zinc-950 hover:bg-amber-300"
            >
              {checkoutBusy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> Abriendo pago…
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" aria-hidden /> Quiero ser VIP
                </>
              )}
            </Button>
            <p className="text-center text-[11px] text-white/35">
              Pago seguro. Si ya pagaste, tocá Actualizar estado.
            </p>
          </SectionCard>
        ) : null}

        {/* Instalación PWA: solo cuando aporta algo. */}
        {pwa.canInstall ? (
          <SectionCard icon={Smartphone} title="Tenelo a mano">
            <p className="text-[13px] leading-relaxed text-white/55">
              Instalá el portal en tu teléfono y entrá de un toque, sin buscar el link.
            </p>
            <Button
              onClick={() => void installApp()}
              variant="outline"
              className="portal-press min-h-11 w-full border-white/15 bg-transparent text-white/75 hover:bg-white/5 hover:text-white"
            >
              Instalar el portal
            </Button>
          </SectionCard>
        ) : pwa.needsIosHint ? (
          <SectionCard icon={Share} title="Tenelo a mano">
            <p className="text-[13px] leading-relaxed text-white/55">
              Tocá <span className="text-white/80">Compartir</span> abajo en Safari y elegí{" "}
              <span className="text-white/80">Agregar a inicio</span>. El portal te queda
              como una app.
            </p>
          </SectionCard>
        ) : null}

        <footer className="space-y-3 px-1 pt-2">
          {email ? (
            <p className="truncate text-center text-[11px] text-white/30">Sesión: {email}</p>
          ) : null}
          <p className="text-center text-[12px] text-white/40">
            ¿Algo no cierra? Escribinos a{" "}
            <a
              className="portal-press text-white/65 underline underline-offset-2"
              href={`mailto:${BRAND_SUPPORT_EMAIL}`}
            >
              {BRAND_SUPPORT_EMAIL}
            </a>
          </p>
          <div className="flex items-center justify-between gap-3 border-t border-white/10 pt-4">
            <a
              href="/"
              className="portal-press inline-flex min-h-11 items-center text-[12px] text-white/45 hover:text-white/80"
            >
              ← Volver al sitio
            </a>
            <SignOutButton>
              <button
                type="button"
                className="portal-press inline-flex min-h-11 items-center gap-1.5 rounded-xl border border-white/15 px-4 text-[12px] font-medium text-white/65 hover:bg-white/5 hover:text-white"
              >
                <LogOut className="h-3.5 w-3.5" aria-hidden /> Cerrar sesión
              </button>
            </SignOutButton>
          </div>
        </footer>
      </div>
    </PortalShell>
  );
}
